import {
  CreateButton,
  DateField,
  DeleteButton,
  List,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useSelect,
  useTable,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  FormProps,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter, useList, useModal } from "@refinedev/core";
import dayjs from "dayjs";
import { IFlagStateCircular } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";

export const FlagStateCircularList: React.FC<IResourceComponentsProps> = () => {
  const [record, setRecord] = React.useState<IFlagStateCircular>();
  const [flagSelected, setFlagSelected] = React.useState<boolean>(false);
  const [flag, setFlag] = React.useState<string>("");

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IFlagStateCircular>({
      resource: "flag-circular",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { title, flag, category, itemNumber, circularNumber, issueDate } =
          params;

        filters.push({
          field: "flag",
          operator: "eq",
          value: flag ? flag : undefined,
        });

        filters.push({
          field: "title",
          operator: "contains",
          value: title ? title : undefined,
        });

        filters.push({
          field: "category",
          operator: "contains",
          value: category ? category : undefined,
        });

        filters.push({
          field: "itemNumber",
          operator: "contains",
          value: itemNumber ? itemNumber : undefined,
        });

        filters.push({
          field: "circularNumber",
          operator: "contains",
          value: circularNumber ? circularNumber : undefined,
        });

        filters.push({
          field: "issueDate",
          operator: "gte",
          value: issueDate
            ? issueDate[0].startOf("day").toISOString()
            : undefined,
        });

        filters.push({
          field: "issueDate",
          operator: "lte",
          value: issueDate
            ? issueDate[1].startOf("day").toISOString()
            : undefined,
        });

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });
  const { show, visible, close } = useModal();

  const columns: ColumnsType<IFlagStateCircular> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 300,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "Flag / IMO",
      dataIndex: "flag",
      key: "flag",
      width: 120,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("flag", sorters),
      sorter: true,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("category", sorters),
      sorter: true,
    },
    {
      title: "Item Number",
      dataIndex: "itemNumber",
      key: "itemNumber",
      width: 120,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("itemNumber", sorters),
      sorter: true,
    },
    {
      title: "Circular Number",
      dataIndex: "circularNumber",
      key: "circularNumber",
      width: 140,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("circularNumber", sorters),
      sorter: true,
    },
    {
      title: "Issue Date",
      dataIndex: "issueDate",
      key: "issueDate",
      width: 140,
      ellipsis: { showTitle: true },
      render: (value) =>
        value ? <DateField format="DD-MMM-YYYY" value={value} /> : "",
      defaultSortOrder: getDefaultSortOrder("issueDate", sorters),
      sorter: true,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 100,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={prettyBytes(value)} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<Icons.DownloadOutlined />}
            href={record?.storageUrl}
            download={record?.fileName}
          />
          <Button
            size="small"
            icon={<Icons.FilePdfOutlined />}
            download={false}
            onClick={() => {
              setRecord(record);
              show();
            }}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
          />
        </Space>
      ),
    },
  ];

  function handleFlagSelection(value: string) {
    setFlagSelected(true);
    setFlag(value);
    searchFormProps?.form?.submit();
    searchFormProps?.form?.resetFields([
      "title",
      "category",
      "itemNumber",
      "circularNumber",
      "issueDate",
    ]);

    //special case as "issueDate" is not reset with form.resetFields() above
    searchFormProps?.form?.setFieldsValue({ issueDate: undefined });
  }

  return (
    <>
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "Digital Bookshelf",
          subTitle: "flag state circulars",
          tags: isLoading ? (
            <Tag color="orange">loading</Tag>
          ) : (
            <Tag color="green">live</Tag>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <CreateButton />
                {flagSelected ? (
                  <Button
                    size="middle"
                    icon={<Icons.FilePdfOutlined />}
                    target="_blank"
                    href={`${process.env.REACT_APP_GCS_FLAG_CIRCULAR_URL || 'https://storage.googleapis.com/emr_flag_circular_v1'}/index/10000%20${flag}.pdf`}
                    download={false}
                  >
                    View Index
                  </Button>
                ) : (
                  <></>
                )}
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xl={24} lg={24} xs={24}>
            <Card type="inner" title={"Filter"}>
              <Filter
                formProps={searchFormProps}
                filters={filters || []}
                flagSelected={flagSelected}
                onSelect={handleFlagSelection}
              />
            </Card>
          </Col>
          <Col xl={24} xs={24}>
            {flagSelected ? (
              <Table
                {...tableProps}
                columns={columns}
                rowKey="id"
                size="middle"
                tableLayout="fixed"
                scroll={{ x: 1190 }}
                sticky
                pagination={{
                  ...tableProps.pagination,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
              />
            ) : (
              <Space
                direction="vertical"
                size="small"
                style={{
                  display: "flex",
                }}
              >
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}></Empty>
                <Alert
                  message="Select Flag State"
                  description="Please select a flag state to view the data"
                  type="info"
                  showIcon
                  style={{
                    margin: "auto",
                    width: "40%",
                    padding: "15px",
                    textAlign: "center",
                    // alignItems: "center",
                  }}
                />
              </Space>
            )}
          </Col>
        </Row>
      </List>
      <Modal
        centered
        closeIcon={<Icons.CloseSquareFilled />}
        visible={visible}
        onCancel={close}
        width="80%"
        footer={null}
      >
        <div>
          <embed
            src={record?.embedUrl}
            type="application/pdf"
            width="99%"
            height="700px"
          />
        </div>
      </Modal>
    </>
  );
};

const Filter: React.FC<{
  formProps: FormProps;
  filters: CrudFilters;
  flagSelected: boolean;
  onSelect: any;
}> = (props) => {
  const { formProps, filters, flagSelected, onSelect } = props;

  const { result: data, query: {
    isLoading
  } } = useList<IFlagStateCircular>({
    resource: "flag-circular",

    pagination: {
      mode: "off"
    }
  });
  const flags = data?.data ?? [];

  const { selectProps: flagSelectProps } = useSelect<IFlagStateCircular>({
    resource: "flag-circular",
    optionLabel: "flag",
    optionValue: "flag",

    sorters: [
      {
        field: "flag",
        order: "asc",
      },
    ],

    pagination: {
      mode: "server",
      pageSize: flags.length || 10
    }
  });

  const { RangePicker } = DatePicker;

  const issueDate = React.useMemo(() => {
    const start = getDefaultFilter("issueDate", filters, "gte");
    const end = getDefaultFilter("issueDate", filters, "lte");

    const startFrom = dayjs(start);
    const endAt = dayjs(end);

    if (start && end) {
      return [startFrom, endAt];
    }
    return undefined;
  }, [filters]);

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        flag: getDefaultFilter("flag", filters),
        title: getDefaultFilter("title", filters),
        category: getDefaultFilter("category", filters),
        itemNumber: getDefaultFilter("itemNumber", filters),
        circularNumber: getDefaultFilter("circularNumber", filters),
        issueDate,
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item
            label={"Flag / IMO"}
            name="flag"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              {...flagSelectProps}
              placeholder="filter by flag / IMO"
              onSelect={onSelect}
              loading={isLoading}
              //disabled={isLoading}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Title"} name="title">
            <Input
              placeholder="filter by title"
              allowClear
              disabled={!flagSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Category"} name="category">
            <Input
              placeholder="filter by category"
              allowClear
              disabled={!flagSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Item Number"} name="itemNumber">
            <Input
              placeholder="filter by item number"
              allowClear
              disabled={!flagSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Circular Number"} name="circularNumber">
            <Input
              placeholder="filter by circular number"
              allowClear
              disabled={!flagSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Issue Date"} name="issueDate">
            <RangePicker
              // defaultValue={undefined}
              value={null}
              style={{ width: "100%" }}
              disabled={!flagSelected}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item
            style={{
              margin: "auto",
              width: "25%",
            }}
          >
            <SaveButton
              disabled={!flagSelected}
              onClick={formProps.form?.submit}
              size="large"
              block
            >
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
